import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { MessageCircle, RefreshCw, Mail, Gift, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const shortDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export const AiChatConversationsTab = () => {
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/ai-chat/sessions?limit=100`);
      setSessions(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      console.error("[ai-chat-sessions] fetch failed", e?.response?.status);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetail = async (sessionId) => {
    setSelected(sessionId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/admin/ai-chat/session/${sessionId}`);
      setDetail(r.data);
    } catch (e) {
      console.error("[ai-chat-detail] fetch failed", e?.response?.status);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => { fetchSessions(); }, []);

  return (
    <div className="space-y-4" data-testid="ai-chat-conversations-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-amber-400" />
            Conversazioni AI
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Tutte le chat dei clienti con Chiara — utile per migliorare il bot, scoprire prodotti richiesti e clienti caldi.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchSessions}
          disabled={loading}
          className="text-gray-300 hover:text-white p-2"
          data-testid="ai-chat-refresh"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-amber-400">
            Sessioni totali: {total} · ultime {sessions.length}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              {loading ? "Caricamento..." : "Nessuna conversazione ancora registrata."}
            </p>
          ) : (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
              {sessions.map((s) => (
                <button
                  key={s.session_id}
                  type="button"
                  onClick={() => fetchDetail(s.session_id)}
                  className={`w-full text-left bg-gray-900/60 border rounded-lg p-3 transition-colors ${
                    selected === s.session_id
                      ? "border-amber-400"
                      : "border-gray-700 hover:border-gray-500"
                  }`}
                  data-testid={`ai-chat-session-${s.session_id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-gray-400">#{s.session_id.slice(-8)}</span>
                      <span className="bg-amber-500/20 text-amber-300 text-[10px] px-2 py-0.5 rounded-full font-semibold">
                        {s.messages_count} msg
                      </span>
                      {s.customer_email && (
                        <span className="bg-emerald-500/20 text-emerald-300 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {s.customer_email}
                        </span>
                      )}
                      {s.coupon_issued && (
                        <span className="bg-purple-500/20 text-purple-300 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Gift className="w-3 h-3" /> {s.coupon_issued}
                        </span>
                      )}
                      {s.products_suggested > 0 && (
                        <span className="bg-blue-500/20 text-blue-300 text-[10px] px-2 py-0.5 rounded-full">
                          🛒 {s.products_suggested} prodotti suggeriti
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-gray-500">{shortDate(s.last_ts)}</span>
                  </div>
                  <p className="text-sm text-gray-300 truncate mt-1">
                    <span className="text-gray-500">U:</span> {s.last_user_msg || "—"}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    <span className="text-gray-500">C:</span> {s.last_assistant_reply || "—"}
                  </p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation drawer */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/70 z-[80] flex items-end md:items-center md:justify-end"
          onClick={() => { setSelected(null); setDetail(null); }}
          data-testid="ai-chat-drawer-backdrop"
        >
          <div
            className="bg-gray-900 border-l border-gray-700 w-full md:max-w-2xl h-[85vh] md:h-full overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="ai-chat-drawer"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
              <div className="min-w-0 flex-1">
                <p className="font-bold">Conversazione</p>
                <p className="text-[11px] opacity-90 font-mono truncate">{selected}</p>
              </div>
              <button
                type="button"
                onClick={() => { setSelected(null); setDetail(null); }}
                className="text-white/80 hover:text-white p-1"
                data-testid="ai-chat-drawer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-900">
              {detailLoading && (
                <p className="text-sm text-gray-500 text-center py-8">Caricamento...</p>
              )}
              {detail && (detail.messages || []).map((m, i) => (
                <div key={i} className="space-y-2">
                  <div className="text-[10px] text-gray-500 text-center">{formatDate(m.ts)}</div>
                  <div className="flex justify-end">
                    <div className="bg-amber-500 text-white text-sm px-3 py-2 rounded-2xl rounded-br-sm max-w-[85%] break-words">
                      {m.user_message}
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-gray-800 text-gray-100 text-sm px-3 py-2 rounded-2xl rounded-bl-sm max-w-[85%] break-words border border-gray-700">
                      {m.assistant_reply}
                      {m.suggested_product_ids && m.suggested_product_ids.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-700 text-[10px] text-blue-300">
                          🛒 Suggeriti: {m.suggested_product_ids.length} prodotti
                        </div>
                      )}
                      {m.coupon_issued && (
                        <div className="mt-2 pt-2 border-t border-gray-700 text-[10px] text-purple-300 flex items-center gap-1">
                          <Gift className="w-3 h-3" /> Coupon emesso: <code>{m.coupon_issued}</code>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {detail && (detail.messages || []).length === 0 && !detailLoading && (
                <p className="text-sm text-gray-500 text-center py-8">Nessun messaggio.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiChatConversationsTab;
